import { apiRequest } from '../lib/api-client';

/** Projects and tasks (context.md sections 33-40). Rows come back snake_case. */

/**
 * The four the server actually stores (`project_status`).
 *
 * This union used to read `PLANNED | ACTIVE | ON_HOLD | DONE | CANCELED`, which agreed with the
 * database on exactly two of them: the screen offered statuses the API refuses and hid two that
 * exist. Kept in step with the enum, because a type that lies is worse than no type.
 */
export type ProjectStatus = 'ACTIVE' | 'ON_HOLD' | 'COMPLETED' | 'ARCHIVED';
export type TaskStatus = 'TODO' | 'IN_PROGRESS' | 'BLOCKED' | 'DONE' | 'CANCELED';
export type TaskPriority = 'LOW' | 'MEDIUM' | 'HIGH' | 'URGENT';

export type ProjectPriority = 'LOW' | 'MEDIUM' | 'HIGH' | 'URGENT';

/**
 * Whether the project is going to land — a different question from whether it is running.
 *
 * A project can be very much active and very much off track. Declared by a person, with a note and
 * a date, so a month-old "at risk" can be seen for what it is rather than believed forever.
 */
export type ProjectHealth = 'ON_TRACK' | 'AT_RISK' | 'OFF_TRACK';

/** Counted from the project's tasks, never typed. */
export interface ProjectProgress {
  tasks: number;
  done: number;
  /** Not done and past due. A project at 80% with nine late tasks is not a project at 80%. */
  overdue: number;
}

export interface ProjectRow {
  id: string;
  name: string;
  description: string | null;
  status: ProjectStatus;
  priority: ProjectPriority;
  health: ProjectHealth;
  health_note: string | null;
  health_set_at: string | null;
  health_set_by: string | null;
  color: string | null;
  start_date: string | null;
  due_date: string | null;
  owner_id: string | null;
  team_id: string | null;
  created_at: string;
  key: string | null;
  uses_sprints: boolean;
  progress: ProjectProgress;
}

/** The project, its numbers and its sprints — one request, because it is one screen. */
export interface ProjectDetail extends ProjectRow {
  sprints: Sprint[];
}

export function getProject(id: string): Promise<ProjectDetail> {
  return apiRequest(`/v1/work/projects/${id}`);
}

export interface TaskRow {
  id: string;
  project_id: string | null;
  parent_task_id: string | null;
  title: string;
  description: string | null;
  status: TaskStatus;
  priority: TaskPriority;
  assignee_id: string | null;
  start_date: string | null;
  due_date: string | null;
  completed_at: string | null;
  estimated_minutes: number | null;
  version: number;
  created_at: string;
  /** Phase 36. */
  sprint_id: string | null;
  item_type: 'EPIC' | 'STORY' | 'TASK' | 'BUG' | 'SUBTASK';
  story_points: number | null;
  number: number | null;
  epic_id: string | null;
}

export function listProjects(): Promise<{ projects: ProjectRow[]; next_cursor: string | null }> {
  return apiRequest('/v1/work/projects?limit=100');
}

export interface TaskFilters {
  projectId?: string;
  status?: TaskStatus;
  assigneeId?: string;
  dueBefore?: string;
  /** A sprint's id, or the literal `none` for what is still in the backlog. */
  sprintId?: string;
}

export function listTasks(
  filters: TaskFilters = {},
): Promise<{ tasks: TaskRow[]; next_cursor: string | null }> {
  const params = new URLSearchParams({ limit: '100' });

  for (const [key, value] of Object.entries(filters)) {
    if (value) params.set(key, value);
  }

  return apiRequest(`/v1/work/tasks?${params.toString()}`);
}

/**
 * Moving a task.
 *
 * The version travels with the change: two people dragging the same card must not silently overwrite
 * each other, so the server refuses the stale one (context.md section 199).
 */
export function setTaskStatus(
  id: string,
  input: { status: TaskStatus; expectedVersion: number },
): Promise<TaskRow> {
  return apiRequest(`/v1/work/tasks/${id}/status`, { method: 'POST', body: input });
}

export function createTask(input: {
  title: string;
  projectId?: string;
  priority?: TaskPriority;
  itemType?: WorkItemType;
  storyPoints?: number | null;
  epicId?: string | null;
  sprintId?: string | null;
  description?: string | null;
  /** An instant, not a date: the server stores UTC and the tenant reads it in its own timezone. */
  dueDate?: string | null;
  assigneeId?: string | null;
  /**
   * The record the task is about.
   *
   * It is what turns "ligar na terça" into something the deal screen can show, instead of a task
   * loose in a list that nobody working the deal will find.
   */
  related?: { type: 'COMPANY' | 'CONTACT' | 'LEAD' | 'OPPORTUNITY'; id: string } | null;
}): Promise<TaskRow> {
  return apiRequest('/v1/work/tasks', { method: 'POST', body: input });
}

export function createProject(input: {
  name: string;
  description?: string;
  key?: string | null;
  usesSprints?: boolean;
  priority?: ProjectPriority;
}): Promise<ProjectRow> {
  return apiRequest('/v1/work/projects', { method: 'POST', body: input });
}

export function updateProject(
  id: string,
  input: Partial<{
    name: string;
    description: string | null;
    key: string | null;
    usesSprints: boolean;
    status: ProjectStatus;
    priority: ProjectPriority;
    health: ProjectHealth;
    healthNote: string | null;
    ownerId: string | null;
    startDate: string | null;
    dueDate: string | null;
  }>,
): Promise<ProjectRow> {
  return apiRequest(`/v1/work/projects/${id}`, { method: 'PATCH', body: input });
}

// ---------------------------------------------------------------------------
// One task

export interface ChecklistItem {
  id: string;
  title: string;
  done: boolean;
  position: number;
}

export interface TaskLink {
  id: string;
  title: string;
  status: TaskStatus;
}

export interface TaskDetail extends TaskRow {
  checklist: ChecklistItem[];
  watchers: string[];
  depends_on: TaskLink[];
  blocks: TaskLink[];
  total_minutes: number;
}

export function getTask(id: string): Promise<TaskDetail> {
  return apiRequest(`/v1/work/tasks/${id}`);
}

export function updateTask(
  id: string,
  input: Partial<{
    title: string;
    description: string | null;
    priority: TaskPriority;
    dueDate: string | null;
    estimatedMinutes: number | null;
    itemType: WorkItemType;
    storyPoints: number | null;
    epicId: string | null;
  }>,
): Promise<TaskRow> {
  return apiRequest(`/v1/work/tasks/${id}`, { method: 'PATCH', body: input });
}

/**
 * Who is watching, and who it is for.
 *
 * Assignment goes through the ordinary task update rather than a verb of its own: it is a field on
 * the task, and a second way to write a field is a second place for it to go wrong. Watching does
 * have its own endpoints, because a watcher is a row in its own table and adding one is not a change
 * to the task at all.
 */
export function assignTask(id: string, assigneeId: string | null): Promise<TaskRow> {
  return apiRequest(`/v1/work/tasks/${id}`, { method: 'PATCH', body: { assigneeId } });
}

export function watchTask(id: string, userId: string): Promise<void> {
  return apiRequest(`/v1/work/tasks/${id}/watchers`, { method: 'POST', body: { userId } });
}

export function unwatchTask(id: string, userId: string): Promise<void> {
  return apiRequest(`/v1/work/tasks/${id}/watchers/${userId}`, { method: 'DELETE' });
}

export function addChecklistItem(id: string, title: string): Promise<{ id: string }> {
  return apiRequest(`/v1/work/tasks/${id}/checklist`, { method: 'POST', body: { title } });
}

export function toggleChecklistItem(id: string, itemId: string, done: boolean): Promise<void> {
  return apiRequest(`/v1/work/tasks/${id}/checklist/${itemId}`, {
    method: 'PATCH',
    body: { done },
  });
}

export function removeChecklistItem(id: string, itemId: string): Promise<void> {
  return apiRequest(`/v1/work/tasks/${id}/checklist/${itemId}`, { method: 'DELETE' });
}

export interface TaskComment {
  id: string;
  body: string;
  author_id: string | null;
  mentions: string[];
  created_at: string;
}

export function listComments(id: string): Promise<{ comments: TaskComment[] }> {
  return apiRequest(`/v1/work/tasks/${id}/comments?limit=100`);
}

export function addComment(id: string, body: string): Promise<TaskComment> {
  return apiRequest(`/v1/work/tasks/${id}/comments`, { method: 'POST', body: { body } });
}

export interface TimeEntry {
  id: string;
  minutes: number;
  note: string | null;
  started_at: string;
  user_id: string | null;
}

export function getTime(id: string): Promise<{ total_minutes: number; entries: TimeEntry[] }> {
  return apiRequest(`/v1/work/tasks/${id}/time`);
}

export function logTime(id: string, input: { minutes: number; note?: string }): Promise<TimeEntry> {
  return apiRequest(`/v1/work/tasks/${id}/time`, { method: 'POST', body: input });
}

export function startTimer(id: string): Promise<unknown> {
  return apiRequest(`/v1/work/tasks/${id}/timer/start`, { method: 'POST' });
}

export function stopTimer(): Promise<unknown> {
  return apiRequest('/v1/work/timer/stop', { method: 'POST' });
}

// ---------------------------------------------------------------------------
// Sprints, keys and history (Phase 36)
// ---------------------------------------------------------------------------

export type SprintState = 'PLANNED' | 'ACTIVE' | 'COMPLETED';
export type WorkItemType = 'EPIC' | 'STORY' | 'TASK' | 'BUG' | 'SUBTASK';

export const WORK_ITEM_TYPES: WorkItemType[] = ['EPIC', 'STORY', 'TASK', 'BUG', 'SUBTASK'];

export interface SprintProgress {
  items: number;
  done: number;
  dropped: number;
  points: number;
  pointsDone: number;
  unestimated: number;
}

export interface Sprint {
  id: string;
  projectId: string;
  /** Per project, from one — "sprint 14" is how a team refers to it out loud. */
  number: number;
  name: string;
  goal: string | null;
  state: SprintState;
  startsAt: string | null;
  endsAt: string | null;
  startedAt: string | null;
  completedAt: string | null;
  progress: SprintProgress;
}

export interface BacklogItem {
  id: string;
  key: string | null;
  title: string;
  status: TaskRow['status'];
  priority: TaskRow['priority'];
  itemType: WorkItemType;
  storyPoints: number | null;
  assigneeId: string | null;
  epicId: string | null;
  sprintId: string | null;
  dueDate: string | null;
}

export interface Backlog {
  project: { id: string; name: string; key: string | null; usesSprints: boolean };
  sprints: (Sprint & { items: BacklogItem[] })[];
  backlog: BacklogItem[];
}

export function getBacklog(projectId: string): Promise<Backlog> {
  return apiRequest(`/v1/work/backlog?projectId=${projectId}`);
}

export function listSprints(
  projectId: string,
  state?: SprintState,
): Promise<{ sprints: Sprint[] }> {
  return apiRequest(`/v1/work/sprints?projectId=${projectId}${state ? `&state=${state}` : ''}`);
}

export function createSprint(input: {
  projectId: string;
  name: string;
  goal?: string | null;
  startsAt?: string | null;
  endsAt?: string | null;
}): Promise<Sprint> {
  return apiRequest('/v1/work/sprints', { method: 'POST', body: input });
}

export function updateSprint(
  id: string,
  input: { name?: string; goal?: string | null; startsAt?: string | null; endsAt?: string | null },
): Promise<Sprint> {
  return apiRequest(`/v1/work/sprints/${id}`, { method: 'PATCH', body: input });
}

export function startSprint(
  id: string,
  window: { startsAt?: string; endsAt?: string } = {},
): Promise<Sprint> {
  return apiRequest(`/v1/work/sprints/${id}/start`, { method: 'POST', body: window });
}

export function completeSprint(
  id: string,
  moveTo: string | null,
): Promise<Sprint & { moved: number; movedTo: 'BACKLOG' | 'SPRINT' }> {
  return apiRequest(`/v1/work/sprints/${id}/complete`, { method: 'POST', body: { moveTo } });
}

export function deleteSprint(id: string): Promise<void> {
  return apiRequest(`/v1/work/sprints/${id}`, { method: 'DELETE' });
}

export interface BurndownPoint {
  day: string;
  remaining: number;
  scope: number;
  ideal: number;
}

export interface SprintReport {
  sprint: Sprint;
  burndown: BurndownPoint[];
  ledger: {
    kind: 'ADDED' | 'REMOVED' | 'COMPLETED' | 'REOPENED';
    taskId: string;
    title: string;
    storyPoints: number | null;
    actorId: string | null;
    actorName: string | null;
    occurredAt: string;
  }[];
}

export function getSprintReport(id: string): Promise<SprintReport> {
  return apiRequest(`/v1/work/sprints/${id}/report`);
}

export function moveToSprint(taskId: string, sprintId: string | null): Promise<void> {
  return apiRequest(`/v1/work/tasks/${taskId}/sprint`, { method: 'POST', body: { sprintId } });
}

export interface ActivityEntry {
  id: string;
  kind: 'CHANGE' | 'COMMENT' | 'TIME';
  action: string;
  actorType: string;
  actorId: string | null;
  before: Record<string, unknown> | null;
  after: Record<string, unknown> | null;
  body: string | null;
  minutes: number | null;
  occurredAt: string;
}

export function getTaskActivity(id: string): Promise<{ activity: ActivityEntry[] }> {
  return apiRequest(`/v1/work/tasks/${id}/activity`);
}
